import React from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/Button';
import { MapPin, Shield, Server, Users, Leaf } from 'lucide-react';
import { SEO } from '../components/seo/SEO';
import { JsonLd } from '../components/seo/JsonLd';
import { organizationSchema, createBreadcrumbSchema } from '../lib/structured-data';

export function About() {
  return (
    <div className="min-h-screen flex flex-col bg-[var(--color-csai-bg)]">
      <Header />

      <main className="flex-grow">
        <SEO
          title="About Us"
          description="Situs Canada builds a Canadian-focused workbench for CPA file workflows with source-backed outputs and firm review gates."
          canonical="https://situsai.ca/about"
          ogImage="https://situsai.ca/logo.svg"
        />
        <JsonLd data={organizationSchema} />
        <JsonLd data={createBreadcrumbSchema('/about')} />

        <section className="py-20 sm:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[var(--color-csai-surface)] text-[var(--color-csai-primary)] text-sm font-medium mb-8">
            <MapPin className="w-4 h-4" /> Built for Canadian practice workflows
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-[var(--color-csai-accent)] tracking-tight">
            Canadian professionals deserve clear, reviewable AI workflows.
          </h1>
          <p className="text-xl text-[var(--color-csai-muted)] max-w-3xl mx-auto">
            Orca is a workbench for client files, sources, and CPA review rather than a general chatbot.
          </p>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">CPA-first context</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                The product is built around client-work flow, not generic Q&A. Output is meant for CPA verification.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <Server className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Infrastructure posture</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We operate with provider settings aimed at Canadian residency expectations for client-sensitive data.
              </p>
            </div>
            <div className="bg-white border border-gray-200 rounded-2xl p-8">
              <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mb-6">
                <Users className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Built by practitioners</h3>
              <p className="text-gray-600 text-sm leading-relaxed">
                We work with Canadian tax and accounting teams to keep Orca focused on practical filing and review needs.
              </p>
            </div>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="bg-[var(--color-csai-surface)] rounded-2xl p-8 sm:p-12 max-w-3xl mx-auto text-center">
            <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-white text-2xl font-bold">C</span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Carlo Clores</h3>
            <p className="text-sm text-gray-500 mb-6">Founder, Situs Canada</p>
            <p className="text-gray-600 leading-relaxed">
              Orca exists to make CPA file work practical, documented, and reviewable with source-grounded outputs.
            </p>
          </div>
        </section>

        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 mb-6">
            <Leaf className="w-5 h-5 text-green-600" />
            <span className="text-sm font-medium text-green-700">Our commitment</span>
          </div>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            We publish how security and review controls currently work. If a claim requires proof, it should be surfaced with source details and ownership.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/security">
              <Button className="rounded-full px-6">Security</Button>
            </Link>
            <Link to="/contact">
              <Button variant="secondary" className="rounded-full px-6">Get in Touch</Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
